import { API_URL } from "@/common/config";
import JwtService from "@/common/jwt.service";
import axios from "axios";
import Vue from "vue";
import VueAxios from "vue-axios";
import store from "../store";
import router from "../router";
import { LOGOUT, REFRESH_AUTH } from "../store/auth.module";

const tokenClient = axios.create({ baseURL: API_URL });

let refreshPromise = null;

const withRefreshLock = (callback) => {
  if (navigator.locks) {
    return navigator.locks.request("portal-token-refresh", callback);
  }
  return callback();
};

const redirectToLogin = () => {
  if (router.currentRoute.name !== "login") {
    router.push("/login");
    store.dispatch(LOGOUT);
  }
};

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    Vue.axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const { config, response } = error;
        if (!response || response.status !== 401) {
          return Promise.reject(error);
        }

        if (
          !config._retry &&
          config.url !== "token" &&
          JwtService.getRefreshToken()
        ) {
          config._retry = true;
          const failedToken = (config.headers.Authorization || "").replace(
            "Bearer ",
            ""
          );
          return ApiService.refreshAccessToken(failedToken).then(
            (token) => {
              config.headers.Authorization = `Bearer ${token}`;
              return Vue.axios(config);
            },
            () => {
              if (!JwtService.getRefreshToken()) {
                redirectToLogin();
              }
              return Promise.reject(error);
            }
          );
        }

        redirectToLogin();
        return Promise.reject(error);
      }
    );
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    Vue.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
  },

  /**
   * Remove the Authorization header from future HTTP requests
   */
  clearHeader() {
    delete Vue.axios.defaults.headers.common["Authorization"];
  },

  /**
   * Send the refresh token grant to the token endpoint
   * @param params
   * @returns {*}
   */
  refreshToken(params) {
    return tokenClient.post("token", params);
  },

  /**
   * Get a new access token using the stored refresh token
   * @param failedToken
   * @returns {Promise<string>}
   */
  refreshAccessToken(failedToken) {
    if (!refreshPromise) {
      refreshPromise = withRefreshLock(() => {
        const currentToken = JwtService.getToken();
        if (currentToken && currentToken !== failedToken) {
          return currentToken;
        }

        return store
          .dispatch(REFRESH_AUTH)
          .then(({ access_token }) => access_token);
      }).finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  },

  query(resource, params) {
    return Vue.axios.get(resource, params);
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "") {
    return Vue.axios.get(`${resource}/${slug}`);
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    return Vue.axios.post(`${resource}`, params);
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  /**
   * @param resource
   * @param params
   */
  patch(resource, params) {
    return Vue.axios.patch(`${resource}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return Vue.axios.delete(resource).catch((error) => {
      // console.log(error);
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },
};

export default ApiService;
